import type { ReactNode } from "react";
import type { DocumentStepperSurface } from "@/components/shared/finance/DocumentStepper";

interface DocumentCreateOutcomeFrameProps {
  children: ReactNode;
  /** The host's header actions, e.g. the dialog's close; unused on the page surface. */
  headerAccessory?: ReactNode;
  surface?: DocumentStepperSurface;
  title: string;
}

/**
 * Frames a create workflow's outcome screen (the "created" state) the way the
 * stepper frames its steps. On the page surface the screen renders as is; on
 * the dialog surface it gets the same header band with the host's actions,
 * so the dialog keeps its close once the stepper has unmounted.
 */
export function DocumentCreateOutcomeFrame({
  children,
  headerAccessory,
  surface = "page",
  title,
}: DocumentCreateOutcomeFrameProps) {
  if (surface !== "dialog") {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="document-create-outcome">
      <div className="flex shrink-0 items-start justify-between gap-4 border-border/60 border-b bg-gradient-to-br from-brand-accent-1/7 via-background to-brand-accent-2/6 px-5 py-4 sm:px-7">
        {/* Not a heading: the dialog already names itself and the outcome screen
            brings its own h1, so this is the visible label only. */}
        <p className="font-display text-h5">{title}</p>
        {headerAccessory ? (
          <div className="flex shrink-0 items-center gap-3">{headerAccessory}</div>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">{children}</div>
    </div>
  );
}
